/**
 * Common sessionStorage utility functions with prefix validation
 * Provides secure encoding/decoding for different data types
 */

/**
 * Encodes data with a prefix for secure storage
 * @param data - The data to encode
 * @param prefix - The prefix to add for validation (e.g., 'ph', 'em')
 * @returns Base64 encoded string with prefix
 */
export const encodeWithPrefix = (data: string, prefix: string): string => {
  try {
    return btoa(`${prefix}:${data}`);
  } catch {
    return data;
  }
};

/**
 * Decodes data and validates the expected prefix
 * @param storedValue - The stored Base64 value
 * @param prefix - The expected prefix for validation
 * @returns Decoded data if prefix matches, empty string otherwise
 */
export const decodeWithPrefix = (storedValue: string | null, prefix: string): string => {
  if (!storedValue) {
    return '';
  }
  try {
    const decoded = atob(storedValue);
    if (decoded.startsWith(`${prefix}:`)) {
      return decoded.slice(prefix.length + 1);
    }
    return decoded;
  } catch {
    return '';
  }
};

/**
 * Gets stored data from sessionStorage with prefix validation
 * @param key - The sessionStorage key
 * @param prefix - The expected prefix for validation
 * @returns Decoded data if valid, empty string otherwise
 */
export const getStoredData = (key: string, prefix: string): string => {
  try {
    const storedValue = sessionStorage.getItem(key);
    return decodeWithPrefix(storedValue, prefix);
  } catch {
    return '';
  }
};

/**
 * Sets data in sessionStorage with prefix encoding
 * @param key - The sessionStorage key
 * @param data - The data to store
 * @param prefix - The prefix to add for validation
 */
export const setStoredData = (key: string, data: string, prefix: string): void => {
  try {
    if (data) {
      const encoded = encodeWithPrefix(data, prefix);
      sessionStorage.setItem(key, encoded);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.warn(`Failed to store ${key} in sessionStorage:`, error);
  }
};
